import {ReactNode, useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {Address} from '~/common/types/location';

interface MapAddress {
  street: string;
  streetAddress: string;
  city: string;
  state: string;
  zip: string;
  zipCode: string;
  country: string;
  unitNum: string;
}

interface MapLocation extends MapAddress {
  location: string;
  locationName: string;
  placeId: string;
  meetingUrl: string;
  showMap: boolean;
}

interface ReduxFriendlyMapLocation {
  location: string;
  address: Address;
  placeId: string | null;
  meetingUrl: string | null;
  showMap: boolean;
}

export interface UseMapLocationParams {
  location: string;
  address: MapAddress;
  placeId: string;
  meetingUrl: string;
  showMap: boolean;
  hasAddress: boolean;
  hasHttpAddress: boolean;
  hasPhysicalAddress: boolean;
  hasPlaceId: boolean;
  hasLocation: boolean;
  formatAddress: (showAll?: boolean) => ReactNode;
  getNameAndAddress: (showAll: boolean) => ReactNode;
  updateAllLocation: (newData: ReduxFriendlyMapLocation) => void;
  updateLocation: (newLocation: string) => void;
  updateAddress: (newAddress: MapAddress) => void;
  updatePlaceId: (newPlaceId: string) => void;
  updateMeetingUrl: (newMeetingUrl: string) => void;
  updateShowMap: (newShowMap: boolean) => void;
  propagateLocationDetails: () => void;
  resetLocationDetails: () => void;
  removeLocation: () => void;
}

export const useMapLocation = (): UseMapLocationParams => {
  const dispatch = useDispatch();
  const details = useSelector((state: {eventDetails: MapLocation}) => state.eventDetails || {});

  const [location, setLocation] = useState(details.location ?? '');
  const [locationName, setLocationName] = useState(details.locationName ?? '');
  const [street, setStreet] = useState(details.street ?? '');
  const [streetAddress, setStreetAddress] = useState(details.streetAddress ?? '');
  const [city, setCity] = useState(details.city ?? '');
  const [state, setState] = useState(details.state ?? '');
  const [zip, setZip] = useState(details.zip ?? '');
  const [zipCode, setZipCode] = useState(details.zipCode ?? '');
  const [country, setCountry] = useState(details.country ?? '');
  const [unitNum, setUnitNum] = useState(details.unitNum ?? '');
  const [placeId, setPlaceId] = useState(details.placeId ?? '');
  const [meetingUrl, setMeetingUrl] = useState(details.meetingUrl ?? '');
  const [showMap, setShowMap] = useState(details.showMap ?? true);

  useEffect(() => setLocation(details.location ?? ''), [details.location]);
  useEffect(() => setLocationName(details.locationName ?? ''), [details.locationName]);
  useEffect(() => setStreet(details.street ?? ''), [details.street]);
  useEffect(() => setStreetAddress(details.streetAddress ?? ''), [details.streetAddress]);
  useEffect(() => setCity(details.city ?? ''), [details.city]);
  useEffect(() => setState(details.state ?? ''), [details.state]);
  useEffect(() => setZip(details.zip ?? ''), [details.zip]);
  useEffect(() => setZipCode(details.zipCode ?? ''), [details.zipCode]);
  useEffect(() => setCountry(details.country ?? ''), [details.country]);
  useEffect(() => setUnitNum(details.unitNum ?? ''), [details.unitNum]);
  useEffect(() => setPlaceId(details.placeId ?? ''), [details.placeId]);
  useEffect(() => setMeetingUrl(details.meetingUrl ?? ''), [details.meetingUrl]);
  useEffect(() => setShowMap(details.showMap ?? true), [details.showMap]);

  const hasAddress = street?.length > 0 || streetAddress?.length > 0 || city?.length > 0;
  const hasHttpAddress = meetingUrl?.length > 0;
  const hasPhysicalAddress = Boolean(street || (streetAddress && !hasHttpAddress));
  const hasPlaceId = placeId?.length > 0;
  const hasLocation = location?.length > 0 || locationName?.length > 0;

  const updateAllLocation = useCallback((newData: ReduxFriendlyMapLocation) => {
    setLocation(newData.location);
    setStreet(newData.address.street ?? '');
    setStreetAddress(newData.address.street ?? '');
    setCity(newData.address.city ?? '');
    setState(newData.address.state ?? '');
    setZip(newData.address.zip ?? '');
    setZipCode(newData.address.zip ?? '');
    setCountry(newData.address.country ?? '');
    setUnitNum(newData.address.unitNum ?? '');
    setPlaceId(newData.placeId ?? '');
    setMeetingUrl(newData.meetingUrl ?? '');
    setShowMap(newData.showMap ?? true);
  }, []);

  const updateAddress = useCallback((newAddress: MapAddress) => {
    setStreet(newAddress.street);
    setStreetAddress(newAddress.streetAddress);
    setCity(newAddress.city);
    setState(newAddress.state);
    setZip(newAddress.zip);
    setZipCode(newAddress.zipCode);
    setCountry(newAddress.country);
    setUnitNum(newAddress.unitNum);
  }, []);

  const formatAddress = useCallback(
    (showAll = true): ReactNode => {
      if (!hasAddress && !hasHttpAddress && !hasLocation) {
        return '';
      }

      if (hasHttpAddress) {
        return (
          <a href={meetingUrl} target="_blank" rel="noreferrer">
            {meetingUrl}
          </a>
        );
      }

      if (hasLocation && !hasAddress) {
        return <div>{location || locationName}</div>;
      }

      // Build address components
      const streetComponent = street || streetAddress;
      const unitComponent = showAll && unitNum ? ` ${unitNum}` : '';
      const cityStateZip = [city, state, zip || zipCode].filter(Boolean).join(', ');

      // Handle location with partial address
      if (hasLocation && !streetComponent && cityStateZip) {
        return (
          <>
            <div>
              {location || locationName}
              {unitComponent}
            </div>
            <div>{cityStateZip}</div>
          </>
        );
      }

      if (!streetComponent && cityStateZip) {
        return <div>{cityStateZip}</div>;
      }

      if (streetComponent && !cityStateZip) {
        return <div>{streetComponent}</div>;
      }

      return (
        <div>
          {showAll && <div>{location || locationName}</div>}
          {streetComponent}
          {unitComponent}
          <br />
          {cityStateZip}
        </div>
      );
    },
    [
      hasAddress,
      hasHttpAddress,
      hasLocation,
      location,
      locationName,
      street,
      streetAddress,
      city,
      state,
      zip,
      zipCode,
      country,
      meetingUrl,
      unitNum,
    ]
  );

  const getNameAndAddress = useCallback(
    (showAll: boolean) => {
      if (location || locationName) {
        return (
          <>
            <div>{location || locationName}</div>
            {formatAddress(showAll)}
          </>
        );
      }
      return formatAddress();
    },
    [formatAddress, location, locationName]
  );

  const propagateLocationDetails = useCallback(() => {
    dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {
        location,
        address: {
          street,
          city,
          state,
          zip,
          country,
          unitNum,
        },
        showMap,
        placeId,
        meetingUrl,
      } as ReduxFriendlyMapLocation,
    });
  }, [
    location,
    street,
    city,
    state,
    zip,
    country,
    unitNum,
    showMap,
    placeId,
    meetingUrl,
    dispatch,
  ]);

  const resetLocationDetails = useCallback(() => {
    setLocation(details.location ?? '');
    setLocationName(details.locationName ?? '');
    setStreet(details.street ?? '');
    setStreetAddress(details.streetAddress ?? '');
    setCity(details.city ?? '');
    setState(details.state ?? '');
    setZip(details.zip ?? '');
    setZipCode(details.zipCode ?? '');
    setCountry(details.country ?? '');
    setUnitNum(details.unitNum ?? '');
    setPlaceId(details.placeId ?? '');
    setMeetingUrl(details.meetingUrl ?? '');
    setShowMap(details.showMap ?? true);
  }, [details]);

  const removeLocation = useCallback(() => {
    dispatch({
      type: UPDATE_EVENT_DETAILS,
      payload: {
        location: '',
        address: {street: '', city: '', state: '', zip: '', country: '', unitNum: ''},
        showMap: true,
        meetingUrl: null,
        placeId: null,
      },
    });
  }, [dispatch]);

  return {
    location: location ?? locationName,
    address: {street, streetAddress, city, state, zip, zipCode, country, unitNum},
    placeId,
    meetingUrl,
    showMap,
    hasAddress,
    hasHttpAddress,
    hasPhysicalAddress,
    hasPlaceId,
    hasLocation,
    formatAddress,
    getNameAndAddress,
    updateAllLocation,
    updateLocation: setLocation,
    updateAddress,
    updatePlaceId: setPlaceId,
    updateMeetingUrl: setMeetingUrl,
    updateShowMap: setShowMap,
    propagateLocationDetails,
    resetLocationDetails,
    removeLocation,
  };
};
